import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from './UserContext'
import { CATALOG_HOODIE_REFERENCE } from '../components/ReferencePhotoPicker'
import { normalizeReferencePhotoId } from '../constants/referencePhotos'
import { resolveSavedCustomInstructions } from '../utils/savedDesignUtils'
import { isUnloadPromptSuppressed, suppressUnloadPrompt } from '../utils/unloadGuard'
import { placementOf } from '../utils/frontBackDesign'
import { clearDesignHandoff } from '../utils/designHandoff'
import { DESIGN_DRAFT_KEY, purgeLegacyUnscopedData, readScoped, removeScoped, writeScoped } from '../utils/userScopedStorage'

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000

export const DEFAULT_DESIGN_DATA = {
  type: '',
  material: '',
  materialVariant: '',
  quality: '',
  style: '',
  color: '',
  fit: '',
  neckline: '',
  sleeveLength: '',
  hem: '',
  additionalNotes: '',
  designPlacement: '',
  frontDescription: '',
  backDescription: ''
}

// Studio editor session — blank choice + placed elements per garment side
const INITIAL_STUDIO_ELEMENTS = { front: [], back: [] }

const INITIAL_DESIGN_SESSION = {
  designData: { ...DEFAULT_DESIGN_DATA },
  customInstructions: '',
  generatedDesign: null,
  currentStep: 1,
  isEditing: false,
  editInstructions: '',
  editHistory: [],
  presentationMode: 'product',
  modelGender: 'woman',
  referencePhotoId: CATALOG_HOODIE_REFERENCE.id,
  useReferencePhoto: false,
  transferredFrom: null,
  lastSaved: null,
  sessionReady: false,
  openManufacturingOnLoad: false,
  /** Account this in-memory session belongs to. Drafts are only written under this uid. */
  ownerUid: null
}

const INITIAL_STUDIO_SESSION = {
  garmentId: null,
  colorId: 'black',
  /** Gelato size label (S, M, L, …). Required for budget-tier productUid. */
  sizeId: null,
  /** Manufacturing tier that defines the blank sandbox. */
  manufacturingTier: 'budget',
  activeView: 'front',
  elementsByView: { ...INITIAL_STUDIO_ELEMENTS },
  /** AI Designer refine handoff — photoreal base image URL (front). Null = vector blank. */
  aiBaseImage: null,
  aiBaseMeta: null, // { type, color, title, sourceDesignId }
  editingDesignId: null,
  editingDesignTitle: '',
  openManufacturingOnLoad: false
}

const DesignSessionContext = createContext(null)

function hasDesignProgress(session) {
  if (!session) return false
  if (session.generatedDesign) return true
  if (session.currentStep > 1) return true
  if (session.customInstructions?.trim()) return true
  const d = session.designData || {}
  return Boolean(d.type || d.material || d.style || d.color)
}

function hasStudioProgress(session) {
  if (!session?.garmentId) return false
  if (session.aiBaseImage) return true
  const byView = session.elementsByView || {}
  return Object.values(byView).some((els) => Array.isArray(els) && els.length > 0)
}

// Signed-out work stays in memory only, so a shared browser never stores a guest draft.
function readDesignAutosave(uid) {
  if (!uid) return null
  const saved = readScoped(DESIGN_DRAFT_KEY, uid)
  if (!saved) return null
  if (!(Date.now() - saved.timestamp < DRAFT_TTL_MS)) {
    removeScoped(DESIGN_DRAFT_KEY, uid)
    return null
  }
  return saved
}

function sessionFromDraft(saved, uid) {
  return {
    ...INITIAL_DESIGN_SESSION,
    designData: saved.designData || { ...DEFAULT_DESIGN_DATA },
    customInstructions: saved.customInstructions || '',
    generatedDesign: saved.generatedDesign || null,
    currentStep: saved.currentStep || 1,
    isEditing: saved.isEditing || false,
    editInstructions: saved.editInstructions || '',
    editHistory: saved.editHistory || [],
    presentationMode: saved.presentationMode || 'product',
    modelGender: saved.modelGender === 'man' ? 'man' : 'woman',
    referencePhotoId: normalizeReferencePhotoId(saved.referencePhotoId) || CATALOG_HOODIE_REFERENCE.id,
    useReferencePhoto: Boolean(saved.useReferencePhoto),
    lastSaved: saved.timestamp ? new Date(saved.timestamp) : null,
    sessionReady: true,
    ownerUid: uid
  }
}

export function DesignSessionProvider({ children }) {
  const { user } = useUser()
  const uid = user?.uid || null
  const [designSession, setDesignSession] = useState(INITIAL_DESIGN_SESSION)
  const [studioSession, setStudioSession] = useState(INITIAL_STUDIO_SESSION)
  const [designDirty, setDesignDirty] = useState(false)
  const [studioDirty, setStudioDirty] = useState(false)
  const [discardPrompt, setDiscardPrompt] = useState(null)
  const ownerRef = useRef(undefined)
  const designSessionRef = useRef(designSession)
  designSessionRef.current = designSession
  const isDirty = designDirty || studioDirty

  const patchDesignSession = useCallback((patch, { dirty = true } = {}) => {
    setDesignSession((prev) => ({ ...prev, ...patch }))
    if (dirty) setDesignDirty(true)
  }, [])

  const patchStudioSession = useCallback((patch, { dirty = true } = {}) => {
    setStudioSession((prev) => ({ ...prev, ...patch }))
    if (dirty) setStudioDirty(true)
  }, [])

  const markDesignSaved = useCallback(() => {
    setDesignDirty(false)
  }, [])

  const markStudioSaved = useCallback(() => {
    setStudioDirty(false)
  }, [])

  /**
   * Suppress the unsaved-work beforeunload prompt for an intentional full-page leave.
   * Call immediately before window.location.assign / replace (Stripe checkout, etc.).
   */
  const allowNextUnload = useCallback(() => {
    suppressUnloadPrompt()
  }, [])

  const resetDesignSession = useCallback(() => {
    const ownerUid = designSessionRef.current.ownerUid
    if (ownerUid) removeScoped(DESIGN_DRAFT_KEY, ownerUid)
    setDesignSession({ ...INITIAL_DESIGN_SESSION, sessionReady: true, ownerUid })
    setDesignDirty(false)
  }, [])

  const resetStudioSession = useCallback(() => {
    setStudioSession({
      ...INITIAL_STUDIO_SESSION,
      elementsByView: { front: [], back: [] },
      aiBaseImage: null,
      aiBaseMeta: null
    })
    setStudioDirty(false)
  }, [])

  // Reopen a saved studio-editor design for further editing
  const loadStudioDesignIntoSession = useCallback((design, { openManufacturing = false } = {}) => {
    const state = design.studioState || {}
    setStudioSession({
      ...INITIAL_STUDIO_SESSION,
      garmentId: state.garmentId || null,
      colorId: state.colorId || 'black',
      sizeId: state.sizeId || null,
      manufacturingTier: state.manufacturingTier || 'budget',
      activeView: 'front',
      elementsByView: {
        front: state.elementsByView?.front || [],
        back: state.elementsByView?.back || []
      },
      aiBaseImage: state.aiBaseImage || null,
      aiBaseMeta: state.aiBaseMeta || null,
      editingDesignId: design.id || null,
      editingDesignTitle: design.title || '',
      openManufacturingOnLoad: openManufacturing
    })
    setStudioDirty(false)
  }, [])

  /**
   * Refine handoff: load an AI-generated design into the Studio with that image as the base.
   * @param {{ garmentId: string, colorId: string, aiBaseImage: string, aiBaseMeta?: object, title?: string }} payload
   */
  const loadAiDesignIntoStudio = useCallback((payload) => {
    setStudioSession({
      ...INITIAL_STUDIO_SESSION,
      garmentId: payload.garmentId,
      colorId: payload.colorId || 'black',
      sizeId: payload.sizeId || null,
      // Refine from AI Designer is not budget-Gelato constrained (photoreal base)
      manufacturingTier: payload.manufacturingTier || 'luxury',
      activeView: 'front',
      elementsByView: { front: [], back: [] },
      aiBaseImage: payload.aiBaseImage || null,
      aiBaseMeta: payload.aiBaseMeta || null,
      editingDesignId: null,
      editingDesignTitle: payload.title || '',
      openManufacturingOnLoad: false
    })
    setStudioDirty(Boolean(payload.aiBaseImage))
  }, [])

  const loadSavedDesignIntoSession = useCallback((design, { openManufacturing = false } = {}) => {
    const specs = design.specifications || {}
    const userInstructions = resolveSavedCustomInstructions(design)
    setDesignSession({
      ...INITIAL_DESIGN_SESSION,
      designData: {
        ...DEFAULT_DESIGN_DATA,
        type: design.type || specs.type || '',
        material: design.material || specs.material || '',
        materialVariant: specs.materialVariant || '',
        quality: specs.quality || design.quality || '',
        style: design.style || specs.style || '',
        color: design.color || specs.color || '',
        fit: specs.fit || '',
        neckline: specs.neckline || '',
        sleeveLength: specs.sleeveLength || '',
        additionalNotes: specs.additionalNotes || design.additionalNotes || '',
        designPlacement: placementOf(design),
        frontDescription: design.frontDescription || '',
        backDescription: design.backDesign === true ? design.backDescription || '' : ''
      },
      customInstructions: userInstructions,
      generatedDesign: {
        ...design,
        mainImage: design.mainImage || design.image || '',
        specifications: specs,
        customPrompt: userInstructions
      },
      currentStep: 3,
      presentationMode: design.presentationMode || specs.presentationMode || 'product',
      modelGender: design.modelGender || specs.modelGender || 'woman',
      sessionReady: true,
      openManufacturingOnLoad: openManufacturing,
      ownerUid: designSessionRef.current.ownerUid
    })
    setDesignDirty(false)
  }, [])

  const confirmDiscardWork = useCallback((message, { onDiscard, onSave } = {}) => {
    setDiscardPrompt({ message, onDiscard, onSave })
  }, [])

  const requestNewDesign = useCallback(
    (onConfirmed) => {
      if (!hasDesignProgress(designSession)) {
        resetDesignSession()
        onConfirmed?.()
        return
      }
      confirmDiscardWork('Starting a new design will replace your current in-progress work.', {
        onDiscard: () => {
          resetDesignSession()
          onConfirmed?.()
        }
      })
    },
    [confirmDiscardWork, designSession, resetDesignSession]
  )

  const hasUnsavedWork = useMemo(
    () =>
      (designDirty && hasDesignProgress(designSession)) ||
      (studioDirty && hasStudioProgress(studioSession)),
    [designDirty, studioDirty, designSession, studioSession]
  )

  // The in-memory sessions belong to one account. When the account changes, drop them and
  // load only the new account's draft. Work started while signed out follows the sign-in.
  useEffect(() => {
    const previous = ownerRef.current
    if (previous === uid) return
    ownerRef.current = uid
    if (previous === undefined) purgeLegacyUnscopedData()

    if (previous === null && uid && hasDesignProgress(designSessionRef.current)) {
      patchDesignSession({ ownerUid: uid }, { dirty: false })
      return
    }

    if (previous) {
      clearDesignHandoff()
      resetStudioSession()
      setDiscardPrompt(null)
    }
    const saved = readDesignAutosave(uid)
    setDesignSession(
      saved
        ? sessionFromDraft(saved, uid)
        : { ...INITIAL_DESIGN_SESSION, sessionReady: previous !== undefined, ownerUid: uid }
    )
    // Only a draft explicitly marked unsaved should warn. Older autosaves have no flag.
    setDesignDirty(saved?.unsaved === true)
  }, [uid, patchDesignSession, resetStudioSession])

  // Backup to localStorage (refresh recovery). In-app nav uses context memory.
  useEffect(() => {
    if (!designSession.ownerUid) return
    if (!designSession.sessionReady) return
    if (!hasDesignProgress(designSession)) return
    writeScoped(DESIGN_DRAFT_KEY, designSession.ownerUid, {
      designData: designSession.designData,
      customInstructions: designSession.customInstructions,
      generatedDesign: designSession.generatedDesign,
      currentStep: designSession.currentStep,
      isEditing: designSession.isEditing,
      editInstructions: designSession.editInstructions,
      editHistory: designSession.editHistory,
      presentationMode: designSession.presentationMode,
      modelGender: designSession.modelGender,
      referencePhotoId: designSession.referencePhotoId,
      useReferencePhoto: designSession.useReferencePhoto,
      unsaved: designDirty,
      timestamp: Date.now()
    })
  }, [designSession, designDirty])

  // Warn only when this tab holds unsaved design or studio work.
  // Intentional redirects call suppressUnloadPrompt() first.
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (isUnloadPromptSuppressed()) return
      if (!hasUnsavedWork) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasUnsavedWork])

  // Dev-only: diagnose scripts can inject an AI refine base without going through CustomAIDesigner
  useEffect(() => {
    if (!import.meta.env.DEV) return undefined
    window.__styleAiLoadAiIntoStudio = loadAiDesignIntoStudio
    return () => {
      delete window.__styleAiLoadAiIntoStudio
    }
  }, [loadAiDesignIntoStudio])

  const value = useMemo(
    () => ({
      designSession,
      studioSession,
      patchDesignSession,
      patchStudioSession,
      resetDesignSession,
      resetStudioSession,
      loadSavedDesignIntoSession,
      loadStudioDesignIntoSession,
      loadAiDesignIntoStudio,
      requestNewDesign,
      confirmDiscardWork,
      markDesignSaved,
      markStudioSaved,
      allowNextUnload,
      hasUnsavedWork,
      isDirty
    }),
    [
      designSession,
      studioSession,
      patchDesignSession,
      patchStudioSession,
      resetDesignSession,
      resetStudioSession,
      loadSavedDesignIntoSession,
      loadStudioDesignIntoSession,
      loadAiDesignIntoStudio,
      requestNewDesign,
      confirmDiscardWork,
      markDesignSaved,
      markStudioSaved,
      allowNextUnload,
      hasUnsavedWork,
      isDirty
    ]
  )

  return (
    <DesignSessionContext.Provider value={value}>
      {children}
      {discardPrompt && (
        <DiscardWorkModal
          message={discardPrompt.message}
          onCancel={() => setDiscardPrompt(null)}
          onDiscard={() => {
            discardPrompt.onDiscard?.()
            setDiscardPrompt(null)
          }}
          onSave={
            discardPrompt.onSave
              ? async () => {
                  await discardPrompt.onSave?.()
                  setDiscardPrompt(null)
                }
              : undefined
          }
        />
      )}
    </DesignSessionContext.Provider>
  )
}

function DiscardWorkModal({ message, onCancel, onDiscard, onSave }) {
  const { user, saveDesign } = useUser()
  const { designSession, markDesignSaved } = useDesignSession()
  const [saving, setSaving] = useState(false)

  const canSave = Boolean(onSave || (user && designSession.generatedDesign))

  const handleSave = async () => {
    if (onSave) {
      await onSave()
      return
    }
    if (!designSession.generatedDesign || !user) return
    setSaving(true)
    try {
      await saveDesign(designSession.generatedDesign)
      markDesignSaved()
      onCancel()
    } catch (_) {
      // saveDesign already told the customer. The design and this dialog stay open.
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-ivory/10 bg-ink-soft p-6 text-ivory shadow-2xl shadow-black/60" role="dialog" aria-modal="true">
        <h2 className="mb-2 font-serif text-xl font-medium text-ivory">Unsaved work</h2>
        <p className="mb-6 text-sm leading-relaxed text-ivory-muted">{message}</p>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          {canSave && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          <button
            type="button"
            onClick={onDiscard}
            className="btn-secondary"
          >
            Discard
          </button>
        </div>
      </div>
    </div>
  )
}

export function useDesignSession() {
  const ctx = useContext(DesignSessionContext)
  if (!ctx) {
    throw new Error('useDesignSession must be used within DesignSessionProvider')
  }
  return ctx
}

export function useDesignSessionFields() {
  const { designSession, patchDesignSession, markDesignSaved, requestNewDesign } = useDesignSession()

  const setDesignData = (updater) => {
    patchDesignSession({
      designData:
        typeof updater === 'function' ? updater(designSession.designData) : updater
    })
  }

  const setCustomInstructions = (value) => patchDesignSession({ customInstructions: value })
  const setGeneratedDesign = (value) => patchDesignSession({ generatedDesign: value })
  const setCurrentStep = (value) => patchDesignSession({ currentStep: value })
  const setIsEditing = (value) => patchDesignSession({ isEditing: value })
  const setEditInstructions = (value) => patchDesignSession({ editInstructions: value })
  const setEditHistory = (updater) => {
    patchDesignSession({
      editHistory:
        typeof updater === 'function' ? updater(designSession.editHistory) : updater
    })
  }
  const setPresentationMode = (value) => patchDesignSession({ presentationMode: value })
  const setModelGender = (value) => patchDesignSession({ modelGender: value })
  const setReferencePhotoId = (value) => patchDesignSession({ referencePhotoId: value })
  const setUseReferencePhoto = (value) => patchDesignSession({ useReferencePhoto: value })
  const setTransferredFrom = (value) => patchDesignSession({ transferredFrom: value })
  const setLastSaved = (value) => patchDesignSession({ lastSaved: value }, { dirty: false })

  const markSessionReady = () => patchDesignSession({ sessionReady: true }, { dirty: false })

  return {
    designData: designSession.designData,
    customInstructions: designSession.customInstructions,
    generatedDesign: designSession.generatedDesign,
    currentStep: designSession.currentStep,
    isEditing: designSession.isEditing,
    editInstructions: designSession.editInstructions,
    editHistory: designSession.editHistory,
    presentationMode: designSession.presentationMode,
    modelGender: designSession.modelGender,
    referencePhotoId: designSession.referencePhotoId,
    useReferencePhoto: designSession.useReferencePhoto,
    transferredFrom: designSession.transferredFrom,
    lastSaved: designSession.lastSaved,
    sessionReady: designSession.sessionReady,
    openManufacturingOnLoad: designSession.openManufacturingOnLoad,
    setDesignData,
    setCustomInstructions,
    setGeneratedDesign,
    setCurrentStep,
    setIsEditing,
    setEditInstructions,
    setEditHistory,
    setPresentationMode,
    setModelGender,
    setReferencePhotoId,
    setUseReferencePhoto,
    setTransferredFrom,
    setLastSaved,
    markSessionReady,
    markSaved: markDesignSaved,
    requestNewDesign,
    patchDesignSession
  }
}

export function useStudioSessionFields() {
  const { studioSession, patchStudioSession, resetStudioSession, markStudioSaved, confirmDiscardWork } =
    useDesignSession()

  const setGarment = (garmentId, colorId, extra = {}) =>
    patchStudioSession({
      garmentId,
      ...(colorId ? { colorId } : {}),
      ...(extra.sizeId !== undefined ? { sizeId: extra.sizeId } : {}),
      ...(extra.manufacturingTier !== undefined
        ? { manufacturingTier: extra.manufacturingTier }
        : {}),
      activeView: 'front',
      // Picking a fresh blank clears any AI refine base
      aiBaseImage: null,
      aiBaseMeta: null
    }, { dirty: false })
  const setColorId = (value) => patchStudioSession({ colorId: value })
  const setSizeId = (value) => patchStudioSession({ sizeId: value })
  const setManufacturingTier = (value) =>
    patchStudioSession(
      {
        manufacturingTier: value,
        // Changing tier resets blank selection so pickers stay coherent
        garmentId: null,
        sizeId: null,
        colorId:
          value === 'budget' || value === 'mid' || value === 'premium' ? 'white' : 'black',
        aiBaseImage: null,
        aiBaseMeta: null,
        elementsByView: { front: [], back: [] },
        editingDesignId: null,
        editingDesignTitle: ''
      },
      { dirty: false }
    )
  const setActiveView = (value) => patchStudioSession({ activeView: value }, { dirty: false })
  const setElementsForView = (view, elements) =>
    patchStudioSession({
      elementsByView: { ...studioSession.elementsByView, [view]: elements }
    })
  const setElementsByView = (value) => patchStudioSession({ elementsByView: value })
  const setAiBaseImage = (url, meta) =>
    patchStudioSession({
      aiBaseImage: url,
      ...(meta !== undefined ? { aiBaseMeta: meta } : {})
    })
  const clearOpenManufacturingFlag = () =>
    patchStudioSession({ openManufacturingOnLoad: false }, { dirty: false })

  return {
    garmentId: studioSession.garmentId,
    colorId: studioSession.colorId,
    sizeId: studioSession.sizeId,
    manufacturingTier: studioSession.manufacturingTier || 'budget',
    activeView: studioSession.activeView,
    elementsByView: studioSession.elementsByView,
    aiBaseImage: studioSession.aiBaseImage,
    aiBaseMeta: studioSession.aiBaseMeta,
    editingDesignId: studioSession.editingDesignId,
    editingDesignTitle: studioSession.editingDesignTitle,
    openManufacturingOnLoad: studioSession.openManufacturingOnLoad,
    setGarment,
    setColorId,
    setSizeId,
    setManufacturingTier,
    setActiveView,
    setElementsForView,
    setElementsByView,
    setAiBaseImage,
    clearOpenManufacturingFlag,
    resetStudioSession,
    markSaved: markStudioSaved,
    confirmDiscardWork,
    patchStudioSession,
    hasStudioWork: hasStudioProgress(studioSession)
  }
}

export { hasDesignProgress, hasStudioProgress, persistDesignSessionToStorage }

function persistDesignSessionToStorage(state, uid) {
  if (!uid) return
  writeScoped(DESIGN_DRAFT_KEY, uid, {
    ...state,
    unsaved: state.unsaved !== false,
    timestamp: Date.now()
  })
}
